import { UnidadMedida } from '@/enum/unidad_medida';

/**
 * COTI-635 — Necesidad de un material teórico de Orden de Producción en la unidad del RECURSO
 * del proceso (p. ej. pliegos en una Komori), con respaldo en la unidad propia del material.
 *
 * El backend devuelve por cada material teórico las filas `OrdenProduccionCantidadElementoProcMatUnidad`
 * (una por tier de cantidad y unidad, con `Total` ya calculado por el motor). Este módulo elige
 * qué fila mostrar y arma el resumen Necesidad / Real / Diferencia en UNA sola unidad, sin
 * ninguna conversión por `FactorConversion` (esa lógica sigue en `material_unidades_conversion.js`
 * y queda sólo para Estructura Técnica). Cuando el recurso cambia y un real queda en otra unidad,
 * `convertirRealAUnidad` lo convierte con el cociente de los `Total` que el motor ya calculó para
 * el mismo teórico (no con `FactorConversion`).
 *
 * Son funciones PURAS: reciben objetos planos, no leen `props`, `ref`, stores ni `navigator`
 * (el formateo numérico lo inyecta quien llama) — mismo patrón que `material_unidades_conversion.js`.
 */

export const CAMPO_FILAS_POR_UNIDAD = 'OrdenProduccionCantidadElementoProcMatUnidad';
export const PRECISION_CANTIDAD = 3;

export const ORIGEN_NECESIDAD = Object.freeze({
  RECURSO: 'recurso',
  PROPIA: 'propia',
  SIN_FILAS: 'sin-filas',
});

export const MOTIVO_RESPALDO = Object.freeze({
  SIN_UNIDAD_RECURSO: 'sin-unidad-recurso',
  SIN_FILA_EN_UNIDAD_RECURSO: 'sin-fila-en-unidad-recurso',
  SIN_FILAS: 'sin-filas',
});

// Unidades que se compran/planifican enteras: la necesidad se muestra redondeada hacia arriba.
const UNIDADES_SIN_FRACCION = Object.freeze([UnidadMedida.HOJA, UnidadMedida.PLIEGO]);

function mismaUnidad(a, b) {
  return a != null && b != null && Number(a) === Number(b);
}

// Igual que el mapper de reales del backend: la relación `UnidadMedida` (que el GET trae
// expandida) tiene prioridad sobre `CodigoUnidadMedida`.
function codigoUnidadDelReal(real) {
  return real?.UnidadMedida?.Codigo ?? real?.CodigoUnidadMedida ?? null;
}

export function redondear3(valor) {
  return Number(Number(valor || 0).toFixed(PRECISION_CANTIDAD));
}

/**
 * Valor listo para mostrar. En hoja/pliego aplica `ceil` sobre el valor YA redondeado a 3
 * decimales: así un residuo de punto flotante (`132079.0000001`) no inventa un pliego de más.
 * Devuelve `null` si el valor es `null`/`undefined` o no es finito.
 */
export function redondearParaMostrar(valor, codigoUnidad) {
  if (valor == null || !Number.isFinite(Number(valor))) return null;
  const valor3 = redondear3(valor);
  return UNIDADES_SIN_FRACCION.includes(Number(codigoUnidad)) ? Math.ceil(valor3) : valor3;
}

/** Filas del tier primario: el de menor `CodigoOrdenProduccionCantidad`. `[]` si no hay filas. */
export function filasTierPrimario(teorico) {
  const filas = Array.isArray(teorico?.[CAMPO_FILAS_POR_UNIDAD])
    ? teorico[CAMPO_FILAS_POR_UNIDAD]
    : [];
  if (!filas.length) return [];
  const tier = Math.min(...filas.map((fila) => Number(fila.CodigoOrdenProduccionCantidad)));
  return filas.filter((fila) => Number(fila.CodigoOrdenProduccionCantidad) === tier);
}

/**
 * Unidad en la que se muestra y se carga el material:
 * - `recurso`: fila de la unidad del recurso con `Total` utilizable.
 * - `propia`: respaldo en la fila `EsUnidadPropia` (con el motivo del respaldo).
 * - `sin-filas`: no hay filas; unidad principal del material, sin necesidad.
 */
export function unidadActiva(teorico, unidadRecurso) {
  const filas = filasTierPrimario(teorico);
  const codigoRecurso = unidadRecurso?.Codigo ?? null;
  const filaRecurso = filas.find(
    (fila) => mismaUnidad(fila.CodigoUnidadMedida, codigoRecurso) && fila.Total != null,
  );
  if (filaRecurso) {
    return {
      codigo: Number(codigoRecurso),
      abreviatura: filaRecurso.UnidadMedida?.Abreviatura || unidadRecurso?.Abreviatura || '',
      origen: ORIGEN_NECESIDAD.RECURSO,
      motivo: null,
      fila: filaRecurso,
    };
  }
  const filaPropia = filas.find((fila) => fila.EsUnidadPropia);
  if (filaPropia) {
    return {
      codigo: Number(filaPropia.CodigoUnidadMedida),
      abreviatura: filaPropia.UnidadMedida?.Abreviatura || '',
      origen: ORIGEN_NECESIDAD.PROPIA,
      motivo:
        codigoRecurso == null
          ? MOTIVO_RESPALDO.SIN_UNIDAD_RECURSO
          : MOTIVO_RESPALDO.SIN_FILA_EN_UNIDAD_RECURSO,
      fila: filaPropia,
    };
  }
  const material = teorico?.Material;
  return {
    codigo: material?.CodigoUnidadMedida ?? null,
    abreviatura: material?.UnidadMedida?.Abreviatura || '',
    origen: ORIGEN_NECESIDAD.SIN_FILAS,
    motivo: MOTIVO_RESPALDO.SIN_FILAS,
    fila: null,
  };
}

/**
 * Suma plana de los reales cargados en la unidad activa. Los demás (incluido uno sin unidad)
 * no se suman: se devuelven en `enOtraUnidad` para avisarlo en la pantalla. La conversión de un
 * real a la unidad activa es un paso aparte (`convertirRealAUnidad`), no ocurre acá.
 */
export function sumaReales(reales, codigoUnidad) {
  const lista = Array.isArray(reales) ? reales : [];
  const enUnidad = lista.filter((real) => mismaUnidad(codigoUnidadDelReal(real), codigoUnidad));
  return {
    suma: enUnidad.reduce((acc, real) => acc + (Number(real?.Cantidad) || 0), 0),
    enOtraUnidad: lista.filter((real) => !enUnidad.includes(real)),
  };
}

function totalUtilizable(fila) {
  if (fila?.Total == null) return null;
  const total = Number(fila.Total);
  return Number.isFinite(total) && total > 0 ? total : null;
}

/**
 * COTI-635: factor para pasar una cantidad de `codigoOrigen` a `codigoDestino` en un teórico:
 * `Total` crudo de la fila destino / `Total` crudo de la fila origen (tier primario). Devuelve
 * `null` si falta alguna fila o algún `Total` es nulo, no finito o <= 0.
 */
export function factorConversionEntreUnidades(teorico, codigoOrigen, codigoDestino) {
  const filas = filasTierPrimario(teorico);
  const filaOrigen = filas.find((fila) => mismaUnidad(fila.CodigoUnidadMedida, codigoOrigen));
  const filaDestino = filas.find((fila) => mismaUnidad(fila.CodigoUnidadMedida, codigoDestino));
  const totalOrigen = totalUtilizable(filaOrigen);
  const totalDestino = totalUtilizable(filaDestino);
  if (totalOrigen == null || totalDestino == null) return null;
  return totalDestino / totalOrigen;
}

/**
 * COTI-635: cantidad del real expresada en la unidad `codigoDestino` del teórico, redondeada a 3
 * decimales. En hoja/pliego se redondea al entero MÁS CERCANO (no `ceil`): son unidades enteras y
 * la ida y vuelta por otra unidad (15 Pl → 0.4 Kg → 15.015 Pl) arrastra el residuo de los 3
 * decimales; `ceil` lo convertiría en un pliego de más. Devuelve `null` si no hay nada que
 * convertir o no se puede: real sin unidad, ya en la unidad destino, cantidad no finita o sin
 * factor.
 */
export function convertirRealAUnidad(real, teorico, codigoDestino) {
  const codigoReal = codigoUnidadDelReal(real);
  if (codigoReal == null || codigoDestino == null) return null;
  if (mismaUnidad(codigoReal, codigoDestino)) return null;
  const cantidad = Number(real?.Cantidad ?? 0);
  if (!Number.isFinite(cantidad)) return null;
  const factor = factorConversionEntreUnidades(teorico, codigoReal, codigoDestino);
  if (factor == null) return null;
  const convertida = redondear3(cantidad * factor);
  return UNIDADES_SIN_FRACCION.includes(Number(codigoDestino))
    ? Math.round(convertida)
    : convertida;
}

/**
 * Resumen único por material teórico: unidad, necesidad, real y diferencia, todo en la unidad
 * activa. La diferencia se calcula sobre la necesidad MOSTRADA (no sobre el `Total` crudo) y es
 * `null` si no hay necesidad.
 */
export function resumenCantidades(teorico, unidadRecurso) {
  const unidad = unidadActiva(teorico, unidadRecurso);
  const necesidad = redondearParaMostrar(unidad.fila?.Total, unidad.codigo);
  const { suma, enOtraUnidad } = sumaReales(teorico?.MaterialesReales, unidad.codigo);
  const real = redondear3(suma);
  return {
    unidad,
    necesidad,
    real,
    diferencia: necesidad == null ? null : redondear3(necesidad - real),
    realesEnOtraUnidad: enOtraUnidad,
  };
}

/** `true` si el real no está cargado en la unidad activa (`unidad` es el objeto de `unidadActiva`). */
export function realEnOtraUnidad(real, unidad) {
  return !mismaUnidad(codigoUnidadDelReal(real), unidad?.codigo);
}

/** Cantidad con la que nace el primer real: la necesidad mostrada, o 0 si no hay necesidad. */
export function semillaPrimerReal(teorico, unidadRecurso) {
  return resumenCantidades(teorico, unidadRecurso).necesidad ?? 0;
}

/** `"<valor formateado> <abrev>"`, o `'—'` si no hay valor. */
export function textoCantidad(valor, abreviatura, formatear = String) {
  if (valor == null) return '—';
  return `${formatear(valor)} ${abreviatura ?? ''}`.trim();
}

/** Texto del tooltip de respaldo, o `null` si la unidad viene del recurso. */
export function mensajeOrigen(unidad, unidadRecurso) {
  if (!unidad || unidad.origen === ORIGEN_NECESIDAD.RECURSO) return null;
  switch (unidad.motivo) {
    case MOTIVO_RESPALDO.SIN_UNIDAD_RECURSO:
      return 'El proceso no tiene un recurso con unidad de medida: se muestra la unidad propia del material.';
    case MOTIVO_RESPALDO.SIN_FILA_EN_UNIDAD_RECURSO:
      return `Este material no tiene cantidad calculada en ${unidadRecurso?.Abreviatura ?? ''}: se muestra en su unidad propia.`;
    case MOTIVO_RESPALDO.SIN_FILAS:
      return 'Todavía no hay cantidades por unidad para este material. Guarde la orden para calcularlas.';
    default:
      return null;
  }
}
